import { useEffect, useMemo, useReducer, type FocusEvent } from 'react';
import {
  monthAYearEarlier,
  monthLabel,
  rangeLabel,
  type RevenueTrendMonth,
} from '@servwell/shared';
import { money, moneyAxis } from '../../money';
import { Change, changeWords } from '../../ui/Change';
import { Skeleton } from '../../ui/Skeleton';
import { chartGeometry, type ChartMonth } from './chartGeometry';
import { NO_TIP, chartTip, openTip } from './chartTip';
import { REVENUE_SECTION, chartWindowWords, monthWords, toThe } from './onScreen';

/**
 * The Revenue page's chart (ops insights Phase 2, ticket 05): the twelve calendar months ending
 * with the month that holds the period's last day, each a bar for what was invoiced (or, under
 * Collected, collected: ticket 07, `label`) and a tick for the same days a year earlier. The server sends every month, zero-filled, the month in
 * progress with the day it stops on, and the day its year earlier stops on; this draws them,
 * hand-rolled, as prototype 201 A did, and works out no date. No chart library: the page needs
 * twelve bars and twelve ticks, and a library would bring its own idea of a colour, a tooltip
 * and a keyboard.
 *
 * ── Reading it without seeing it ────────────────────────────────────────────
 *
 *   Each month is a real `<button>`, in the tab order, with the global focus ring. Its
 *   accessible name says everything the marks and the tooltip show, whole: "Sep 2026, to the
 *   28th: $1,500.00. Sep 1–28, 2025: $1,000.00. Up 50.0%." (`monthName`, the change in the
 *   words `Change` draws, `changeWords`). The marks, the month letters and the tooltip are
 *   `aria-hidden`: they repeat the name, and a screen reader would otherwise read each month
 *   twice.
 *
 *   A click, Enter or Space opens the month's drill (ticket 06), the invoices behind its bar,
 *   in the side panel, and the button says it opens one (`aria-haspopup="dialog"`). Only this
 *   year's: the year-earlier tick is part of the month's name, and opens nothing of its own.
 *
 * ── The tooltip ─────────────────────────────────────────────────────────────
 *
 *   Which one is open is `chartTip`'s (pure, tested): hover, or VISIBLE keyboard focus (a click
 *   focuses too, and must not leave a tooltip behind), and Escape hides it until another month
 *   is shown (WCAG 1.4.13). It hangs in a band kept free above the plot, so it covers no bar,
 *   inside the chart's own scroller, from the left for the first months and the right for the
 *   last, so neither edge cuts it off. It is the button's own child, so the pointer can move
 *   onto it without closing it (1.4.13, hoverable).
 *
 *   Not the kit's `components/ui/tooltip` (Radix): it portals its content to the body, out of
 *   this scroller and its band, and ties it to the button with `aria-describedby`, so a screen
 *   reader would read each month's figures twice, once in the name and again as its
 *   description.
 *
 * ── Colour and shape ────────────────────────────────────────────────────────
 *
 *   Tokens only, light and dark (`--sw-chart-bar`, `--sw-chart-year-earlier`,
 *   `.chart-partial-hatch`, `--radius-mark` in `styles/theme.css`). The year earlier is a tick,
 *   a line across the bar and wider than it, so it is told apart by shape and by the legend,
 *   not by colour alone. The month in progress is hatched, and the legend names it ("Sep, to
 *   the 28th"). A figure below zero, which no mark can draw, is written in the mark's place:
 *   this year's in ink, a year earlier's in the tick's colour.
 *
 * ── Width ───────────────────────────────────────────────────────────────────
 *
 *   The chart scrolls inside its own container (`.h-scroll`, the section `min-w-0`), never the
 *   page. At 390 px its twelve columns fit without scrolling, the month names shortened to
 *   their first letter by a container query (`@sm:`), so the month in progress, at the right
 *   end, is on screen without a swipe; it scrolls only below its minimum width.
 */

/** A month's short name, "Sep": the first word of `monthLabel`'s "Sep 2026". */
const shortMonth = (month: string) => monthLabel(month).split(' ')[0]!;

/**
 * The days its tick is for, in words, as the server sent them: the month a year back, "Aug
 * 2025", or, when the server says that figure stops before the month ends
 * (`yearEarlierThrough`), its days, "Sep 1–28, 2025", "Feb 1–28, 2028".
 */
function yearEarlierWords(m: RevenueTrendMonth): string {
  return m.yearEarlierThrough
    ? rangeLabel(`${m.yearEarlierThrough.slice(0, 8)}01`, m.yearEarlierThrough)
    : monthLabel(monthAYearEarlier(m.month));
}

/** A month's accessible name, whole: both years' figures and the change. */
export function monthName(m: RevenueTrendMonth): string {
  return `${monthWords(m)}: ${money(m.cents)}. ${yearEarlierWords(m)}: ${money(m.yearEarlierCents)}. ${changeWords(m.cents, m.yearEarlierCents)}.`;
}

/** Where a month's tooltip hangs, so the first and last months' stay inside the chart. */
function tipAnchor(index: number, count: number): string {
  if (index < 4) return 'left-0';
  if (index >= count - 4) return 'right-0';
  return 'left-1/2 -translate-x-1/2';
}

/**
 * One month: a column the height of the plot, its bar, its tick (or its figures below zero),
 * its letter, and its tooltip when `tipOpen`. No hooks, so a test can call it and press its
 * handlers (LESSONS 2026-09-29); the state is `RevenueChart`'s.
 */
export function MonthColumn({
  month: m,
  index,
  count,
  tipOpen,
  onHover,
  onFocusMove,
  onOpen,
}: {
  month: ChartMonth;
  index: number;
  /** How many months the chart has, for where the tooltip hangs. */
  count: number;
  tipOpen: boolean;
  /** The pointer came onto this month (its index) or left it (null). */
  onHover: (index: number | null) => void;
  /** VISIBLE keyboard focus came onto this month (its index), or focus left it (null). */
  onFocusMove: (index: number | null) => void;
  /** The month was pressed (a click, Enter or Space): open its drill. */
  onOpen: () => void;
}) {
  const short = shortMonth(m.month);
  // The year under the first month and under every January: "’25".
  const yearMark =
    index === 0 || m.month.slice(5) === '01' ? `’${m.month.slice(2, 4)}` : null;
  return (
    <button
      type="button"
      aria-label={monthName(m)}
      aria-haspopup="dialog"
      className="group relative flex h-full w-full flex-col items-center rounded-sm px-0.5 pt-1 hover:bg-surface-2 focus-visible:bg-surface-2"
      onClick={onOpen}
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
      onFocus={(e: FocusEvent<HTMLButtonElement>) => {
        // A click focuses the button too; only focus the keyboard shows opens the tooltip.
        if (e.currentTarget.matches(':focus-visible')) onFocusMove(index);
      }}
      onBlur={() => onFocusMove(null)}
    >
      <span aria-hidden="true" className="relative block h-44 w-full">
        <span
          data-mark="bar"
          className={`absolute bottom-0 left-1/2 w-[min(24px,70%)] -translate-x-1/2 rounded-t-mark bg-[var(--sw-chart-bar)]${m.hatched ? ' chart-partial-hatch' : ''}`}
          style={{ height: `${m.barPct}%` }}
        />
        {m.tickPct === null ? null : (
          <span
            data-mark="year-earlier"
            className="absolute left-1/2 h-[3px] w-[min(34px,94%)] -translate-x-1/2 translate-y-1/2 rounded-full bg-[var(--sw-chart-year-earlier)] shadow-[0_0_0_2px_var(--sw-surface)]"
            style={{ bottom: `${m.tickPct}%` }}
          />
        )}
        {m.belowZero || m.yearEarlierBelowZero ? (
          // No mark can draw a figure below zero, so it is written where the mark would be,
          // just above the bar (the name and the tooltip give it to the cent).
          <span
            className="absolute inset-x-0 flex flex-col items-center gap-px whitespace-nowrap text-[10px] font-semibold leading-none tabular-nums"
            style={{ bottom: `calc(${m.barPct}% + 2px)` }}
          >
            {m.yearEarlierBelowZero ? (
              <span
                data-mark="year-earlier-below-zero"
                className="text-[var(--sw-chart-year-earlier)]"
              >
                {moneyAxis(m.yearEarlierCents)}
              </span>
            ) : null}
            {m.belowZero ? (
              <span data-mark="below-zero" className="text-foreground">
                {moneyAxis(m.cents)}
              </span>
            ) : null}
          </span>
        ) : null}
      </span>
      <span
        aria-hidden="true"
        className="mt-1 flex h-7 flex-col items-center text-[11px] leading-tight text-muted-foreground"
      >
        <span className="@sm:hidden">{short[0]}</span>
        <span className="hidden @sm:inline">{short}</span>
        {yearMark ? <span>{yearMark}</span> : null}
      </span>
      {tipOpen ? <Tip month={m} anchor={tipAnchor(index, count)} /> : null}
    </button>
  );
}

/**
 * A month's tooltip: its figure and change, and the year earlier's. It repeats the name, so it
 * is hidden from a screen reader; it is not hidden from the pointer, which may rest on it.
 */
function Tip({ month: m, anchor }: { month: ChartMonth; anchor: string }) {
  return (
    <span
      aria-hidden="true"
      data-tip=""
      className={`absolute bottom-full z-10 w-52 rounded-[var(--radius)] border border-border bg-popover px-3 py-1.5 text-left text-xs shadow-[var(--shadow-soft)] ${anchor}`}
    >
      <span className="block text-muted-foreground">{monthWords(m)}</span>
      <span className="block">
        <span className="text-sm font-semibold tabular-nums text-foreground">
          {money(m.cents)}
        </span>{' '}
        <Change now={m.cents} yearEarlier={m.yearEarlierCents} />
      </span>
      <span className="block text-muted-foreground">
        {yearEarlierWords(m)}:{' '}
        <span className="tabular-nums">{money(m.yearEarlierCents)}</span>
      </span>
    </span>
  );
}

/**
 * The chart, for the twelve months the server sent and the period's last day (`to`, which the
 * line under the heading names). `onOpenMonth` opens a month's drill.
 */
export function RevenueChart({
  trend,
  to,
  label,
  onOpenMonth,
}: {
  trend: RevenueTrendMonth[];
  to: string;
  /** What the bars measure, as the page names its basis: "Invoiced", "Collected". */
  label: string;
  onOpenMonth: (month: RevenueTrendMonth) => void;
}) {
  const { months, gridlines } = useMemo(() => chartGeometry(trend), [trend]);
  const [tip, dispatch] = useReducer(chartTip, NO_TIP);
  const open = openTip(tip);

  // Escape hides the open tooltip, wherever the pointer and the focus are.
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') dispatch({ type: 'escape' });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const lastMonth = months.at(-1);
  if (!lastMonth) return null;
  const inProgress = lastMonth.partialThrough
    ? `${shortMonth(lastMonth.month)}, ${toThe(lastMonth.partialThrough)}`
    : null;

  return (
    <section aria-labelledby="revenue-by-month" className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="revenue-by-month" className="font-heading text-sm font-semibold">
          {REVENUE_SECTION.byMonth(label)}
        </h2>
        <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <li className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2.5 w-3 rounded-mark bg-[var(--sw-chart-bar)]"
            />
            {label}
          </li>
          <li className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-[3px] w-3.5 rounded-full bg-[var(--sw-chart-year-earlier)]"
            />
            Same days a year earlier
          </li>
          {inProgress ? (
            <li className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="chart-partial-hatch h-2.5 w-3 rounded-mark bg-[var(--sw-chart-bar)]"
              />
              {inProgress}
            </li>
          ) : null}
        </ul>
      </div>
      <p className="text-xs text-muted-foreground">{chartWindowWords(to)}</p>
      <div className="h-scroll rounded-[var(--radius)] border border-border bg-surface">
        {/* `pt-[4.5rem]`: the band the tooltips hang in, above the plot. */}
        <div className="min-w-[18rem] pb-2 pl-12 pr-3 pt-[4.5rem]">
          <div className="relative">
            {/* The gridlines and their figures, behind the months, over the plot alone. */}
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-1 h-44"
            >
              {gridlines.map((g) => (
                <div
                  key={g.cents}
                  className="absolute inset-x-0 border-t border-border"
                  style={{ bottom: `${g.pct}%` }}
                >
                  <span className="absolute right-full top-0 mr-2 -translate-y-1/2 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
                    {moneyAxis(g.cents)}
                  </span>
                </div>
              ))}
            </div>
            <ol className="@container relative grid grid-cols-12 gap-1">
              {months.map((m, i) => (
                <li key={m.month} className="min-w-0">
                  <MonthColumn
                    month={m}
                    index={i}
                    count={months.length}
                    tipOpen={open === i}
                    onHover={(at) => dispatch({ type: 'hover', index: at })}
                    onFocusMove={(at) => dispatch({ type: 'focus', index: at })}
                    onOpen={() => onOpenMonth(m)}
                  />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * The chart's shape while the page loads: its heading, the line under it, and its box at the
 * box's own height, the band (4.5rem) + the column (0.25rem + the plot's 11rem + the letters'
 * 0.25rem + 1.75rem) + 0.5rem below + the border, so the page does not jump when it arrives.
 */
export function RevenueChartSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-80 max-w-full" />
      <Skeleton className="h-[calc(18.25rem+2px)]" />
    </div>
  );
}
